<template>
  <div>
    <v-card-actions class="justify-end" v-if="!loading">
      <v-btn
        v-if="is_officer"
        color="green darken-3"
        class="white--text"
        @click="
          downloadExcelAll(
            computed_teach_table_data,
            selected_semester,
            selected_year,
            level_id
          )
        "
        >{{ $t("teach_table.download_all")
        }}<v-icon> mdi-microsoft-excel</v-icon>
      </v-btn>
    </v-card-actions>

    <v-card
      outlined
      class="mt-3"
      v-for="(panel, index) in computed_teach_table_data"
      :key="index"
    >
      <v-card-text class="px-1 px-md-2 px-xl-2">
        <h2 class="mb-2 deep-orange--text">
          {{ objectTranslatorLowerCase(panel, "faculty_name") }}
        </h2>
        <h2 class="mb-2">
          {{ objectTranslatorLowerCase(panel, "department_name") }}
        </h2>
        <h2
          v-if="isUndergraduate"
          class="mb-2"
          v-html="
            `${objectTranslatorLowerCase(
              panel,
              'curriculum_name'
            )}, ${objectTranslatorLowerCase(panel, 'class')}`
          "
        ></h2>
        <template v-else>
          <h2 v-for="(line, lineIndex) in graduateHeadingLines(panel)" :key="'heading-' + lineIndex" class="mb-2">{{ line }}</h2>
        </template>

        <div v-for="(table, index) in panel.teachtable" :key="index">
          <v-sheet
            color="white"
            elevation="2"
            class="text-left mt-1 pa-1 pl-2 text-h7 deep-orange lighten-3"
            >{{
              objectTranslatorLowerCase(table, "subject_type_name")
            }}</v-sheet
          >

          <v-simple-table dense class="mb-5">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-center" v-if="canManage"></th>
                  <th class="text-center">{{ $t("teach_table.course_id") }}</th>
                  <th class="text-center">
                    {{ $t("teach_table.course_name") }}
                  </th>
                  <th class="text-center" v-tooltip="$t('teach_table.credit2')">
                    {{ $t("teach_table.credit") }}
                    <v-icon size="small">mdi-help-circle-outline</v-icon>
                  </th>
                  <th class="text-center">{{ $t("teach_table.section") }}</th>
                  <th v-if="canManage && isUndergraduate" class="text-center">
                    {{ $t("teach_table.sec_pair") }}
                  </th>
                  <th
                    v-else-if="isUndergraduate && $store.state.user.is_officer === true"
                  >
                    {{ $t("teach_table.sec_pair") }}
                  </th>
                  <th class="text-center">
                    {{ $t("teach_table.teaching_date") }}
                  </th>
                  <th class="text-center">{{ $t("teach_table.room") }}</th>
                  <th class="text-center">{{ $t("teach_table.building") }}</th>
                  <th class="text-center">{{ $t("teach_table.lecturer") }}</th>
                  <th class="text-center">{{ $t("teach_table.exam_date") }}</th>
                  <th class="text-center">{{ $t("teach_table.condition") }}</th>
                  <th class="text-center">{{ $t("teach_table.note") }}</th>
                  <th class="text-center">{{ $t("teach_table.limit") }}</th>
                  <th v-if="isUndergraduate" class="text-center">
                    {{ $t("teach_table.pre_registered") }}<br />{{
                      $t("teach_table.pre_registered2")
                    }}
                  </th>
                  <th v-if="isUndergraduate" class="text-center">
                    {{ $t("teach_table.queue") }}
                  </th>
                  <th class="text-center">
                    {{ $t("teach_table.registered") }}
                  </th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="(item, index) in table.data" :key="index">
                  <td style="white-space: nowrap" v-if="canManage">
                    <v-btn
                      color="orange"
                      icon
                      tile
                      @click="goToEditTeachTable(item.teach_table_id, panel.faculty_id)"
                      v-if="!item.class_group_display"
                      v-tooltip="'แก้ไข'"
                    >
                      <v-icon>mdi-pencil-outline</v-icon>
                    </v-btn>

                    <v-btn
                      color="blue"
                      icon
                      tile
                      @click="goToCopyTeachTable(item.teach_table_id, panel.faculty_id)"
                      v-if="!item.class_group_display"
                      v-tooltip="'คัดลอก'"
                    >
                      <v-icon>mdi-content-copy</v-icon>
                    </v-btn>

                    <v-btn
                      color="red"
                      icon
                      tile
                      @click="
                        cancelTeachTable(
                          item.teach_table_id,
                          item.subject_id,
                          item.section,
                          item.closed,
                          item.registered_count != null ? item.registered_count : item.count
                        )
                      "
                      v-if="!item.class_group_display"
                      v-tooltip="'ลบ'"
                    >
                      <v-icon>mdi-trash-can</v-icon>
                    </v-btn>
                  </td>

                  <td>
                    <div>
                      <a
                        v-if="item.class_group_display"
                        :href="item.href"
                        target="_blank"
                        >{{ item.subject_id }}</a
                      >
                      <span v-else>{{ item.subject_id }}</span>
                    </div>
                  </td>

                  <td>
                    <a
                      style="text-decoration: none"
                      v-on:click="openSubjectDialog(item.subject_id)"
                    >
                      {{ item.subject_name_en }}
                    </a>
                  </td>

                  <td>{{ item.credit_str }}</td>

                  <td style="white-space: nowrap">
                    {{ item.section }}
                    <div>
                      <lect-or-prac-chip :item="item"></lect-or-prac-chip>
                    </div>
                  </td>

                  <td v-if="canManage && isUndergraduate">
                    <section-pair
                      :subjectObject="item"
                      :fetch_data="fetch_data"
                      :year="selected_year"
                      :semester="selected_semester"
                    />
                  </td>
                  <td
                    v-else-if="isUndergraduate && $store.state.user.is_officer === true"
                    style="white-space: nowrap"
                  >
                    {{ item.sec_pair }}
                  </td>

                  <td style="white-space: nowrap">
                    <a
                      style="text-decoration: none"
                      v-on:click="
                        openTimeSlotDialog(
                          item.subject_id,
                          item.teach_table_id,
                          item.section
                        )
                      "
                      v-html="item.classdatetime_table"
                    ></a>
                  </td>

                  <td>
                    <a
                      style="text-decoration: none"
                      v-on:click="
                        goToRoomShow(
                          selected_year,
                          selected_semester,
                          item.building_no,
                          item.room_no
                        )
                      "
                    >
                      {{ item.room_name || item.classroom }}
                    </a>
                    <building-dialog
                      :subjectObject="item"
                      v-if="
                        !item.class_group_display &&
                        check_permission(['config_teach_table']) &&
                        configurableForClassroomAndBuilding
                      "
                      :teach_table_id="item.teach_table_id"
                      :fetch_data="fetch_data"
                      :level-id="level_id"
                    />
                  </td>

                  <td>
                    {{ item.building_name || item.classbuilding }}
                    <building-dialog
                      :subjectObject="item"
                      v-if="
                        !item.class_group_display &&
                        check_permission(['config_teach_table']) &&
                        configurableForClassroomAndBuilding
                      "
                      :teach_table_id="item.teach_table_id"
                      :fetch_data="fetch_data"
                      :level-id="level_id"
                    />
                  </td>

                  <td style="white-space: nowrap">
                    <div
                      v-html="objectTranslatorLowerCase(item, 'teacher_list')"
                    ></div>
                    <v-btn
                      fab
                      x-small
                      elevation="0"
                      v-if="
                        canManage &&
                        !item.class_group_display
                      "
                      color="green"
                      v-on:click="openTeacherDialog(item)"
                    >
                      <v-icon> mdi-account-multiple-plus </v-icon>
                    </v-btn>
                  </td>

                  <td style="white-space: nowrap">
                    <div v-if="!item.class_group_display">
                      <v-chip
                        class="ma-2 white--text ma-0"
                        color="indigo"
                        width="100"
                        x-small
                      >
                        Midterm
                      </v-chip>
                      <div class="indigo--text font-weight-bold">
                        <div>{{ item.midterm_date_formatted }}</div>
                        <div>{{ item.midterm_time_formatted }}</div>
                      </div>
                    </div>
                    <div v-if="!item.class_group_display">
                      <v-chip
                        class="ma-2 white--text ma-0"
                        color="deep-orange"
                        x-small
                      >
                        Final
                      </v-chip>
                      <div class="deep-orange--text font-weight-bold">
                        <div>{{ item.final_date_formatted }}</div>
                        <div>{{ item.final_time_formatted }}</div>
                      </div>
                    </div>
                  </td>

                  <td>
                    <div
                      v-if="
                        shortRule(objectTranslatorLowerCase(item, 'rules')) ==
                        ''
                      "
                    >
                      <div
                        v-html="objectTranslatorLowerCase(item, 'rules')"
                      ></div>
                      <div v-if="objectTranslatorLowerCase(item, 'rules')">
                        <font color="red">
                          {{ $t("teach_table.not_accept_other_students") }}
                        </font>
                      </div>
                    </div>
                    <div v-else>
                      <div
                        v-html="
                          shortRule(objectTranslatorLowerCase(item, 'rules'))
                        "
                      ></div>
                      <div>
                        <font color="red">
                          {{ $t("teach_table.not_accept_other_students") }}
                        </font>
                      </div>
                      <span class="font-weight-bold and-text">
                        {{ $t("teach_table.and") }}
                      </span>
                      <v-btn
                        class="ma-2 white--text rule"
                        rounded
                        small
                        elevation="4"
                        color="red darken-4"
                        v-on:click="
                          showRule(objectTranslatorLowerCase(item, 'rules'))
                        "
                      >
                        {{ $t("teach_table.seecondition") }}
                      </v-btn>
                    </div>
                  </td>

                  <td>
                    <div v-html="item.remark"></div>
                  </td>

                  <td>{{ item.limit }}</td>

                  <td v-if="isUndergraduate">{{ item.pre_count }}</td>

                  <td v-if="isUndergraduate">{{ item.queue_left }}</td>

                  <td class="last-cell">{{ item.count }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>
      </v-card-text>
      <v-card-actions>
        <div
          class="pr-3"
          v-if="isUndergraduate && check_permission(['config_teach_table']) && !configurable"
        >
          <p class="red--text text-center pa-0 ma-0">
            ไม่มีสิทธิ์ตั้งค่าตารางสอนในคณะและปีการศึกษานี้
            โปรดติดต่อเจ้าหน้าที่สำนักทะเบียนฯ
          </p>
        </div>

        <v-btn
          v-if="canManage"
          color="green"
          class="white--text mr-3"
          @click="goToAddTeachTable(panel)"
        >
          {{ $t("teach_table.add_course_to_curr_class") }}
        </v-btn>

        <v-btn
          v-if="check_permission(['config_teach_table'])"
          color="red"
          class="white--text"
          @click="downloadPdf(panel, selected_semester, selected_year, level_id)"
          >{{ $t("teach_table.download") }}<v-icon> mdi-file-pdf-box</v-icon>
        </v-btn>

        <v-btn
          v-if="is_officer"
          color="green darken-3"
          class="white--text"
          @click="downloadExcel(panel, selected_semester, selected_year, level_id)"
          >{{ $t("teach_table.download") }}<v-icon> mdi-microsoft-excel</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>

    <!--Rule-->
    <v-dialog v-model="isRuleOpen" scrollable max-width="500">
      <v-card>
        <v-card-title>
          {{ $t("teach_table.condition") }}
          <font color="red" class="ml-2">
            {{ $t("teach_table.not_accept_other_students") }}
          </font>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="py-2">
          <div v-html="ruleText"></div>
          <div>
            <font color="red">
              {{ $t("teach_table.not_accept_other_students") }}
            </font>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn color="blue darken-1" text @click="isRuleOpen = false">
            {{ $t("pre_regis_add_subject.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <close-teach-table-dialog ref="close_teach_table_dialog" :level-id="level_id" />

    <config-teacher-dialog
      ref="config_teacher_dialog"
      :year="selected_year"
      :semester="selected_semester"
      :fetch_data="fetch_data"
      :teacherData="teacherData"
      :levelId="level_id"
    />

    <subject-dialog ref="subject_dialog" :level-id="level_id" />

    <timeslot-dialog ref="timeslot_dialog" :level-id="level_id" />
  </div>
</template>

<script>
import tableTableProvider from "@/services/teach_table.provider.js";
const tableTableService = new tableTableProvider();
import { check_permission } from "@/router/acl";
import closeTeachTableDialog from "./components/CloseTeachTableDialog.vue";
import configTeacherDialog from "./components/ConfigTeacherDialog.vue";
import "jspdf-autotable";
import "@/jsPDFFonts/THSarabunNew-normal.js";
import {
  downloadPdf,
  downloadExcel,
  downloadExcelAll,
} from "./teachTableDownload";
import sectionPair from "./components/SectionPair.vue";
import subjectDialog from "./components/SubjectDialog.vue";
import timeslotDialog from "./components/TimeSlotDialog.vue";
import BuildingDialog from "./components/BuildingDialog.vue";
import LectOrPracChip from "./components/LectOrPracChip.vue";
import * as dayjs from "dayjs";
require("dayjs/locale/th");
import {
  objectTranslator,
  objectTranslatorLowerCase,
} from "@/helper/translator";

export default {
  components: {
    closeTeachTableDialog,
    configTeacherDialog,
    sectionPair,
    subjectDialog,
    timeslotDialog,
    BuildingDialog,
    LectOrPracChip,
  },
  name: "TeachTableTable",

  props: [
    "teach_table_data",
    "mode",
    "search_all_class_year",
    "search_all_curriculum",
    "search_all_branch",
    "search_all_department",
    "search_all_faculty",
    "selected_class_year",
    "selected_curriculum",
    "selected_branch",
    "selected_department",
    "selected_faculty",
    "selected_semester",
    "selected_year",
    "selected_subject_id",
    "selected_subject_name",
    "selected_subject_owner_id",
    "selected_admission_type",
    "selected_admission_semester",
    "fetch_data",
    "config_permission",
    "loading",
    "level_id",
  ],

  data: () => ({
    is_logged_in: true,
    teacherData: [],
    isRuleOpen: false,
    ruleText: "",
  }),

  computed: {
    isUndergraduate() {
      return Number(this.level_id || 1) === 1;
    },
    canManage() {
      return (
        this.check_permission(["config_teach_table"]) &&
        this.configurable &&
        this.hasNativeTeachTableId
      );
    },
    hasNativeTeachTableId() {
      return this.teach_table_data.some((panel) =>
        (panel.teachtable || []).some((group) =>
          (group.data || []).some((item) => item.teach_table_id)
        )
      );
    },
    is_officer() {
      return this.$store.state.user.is_officer;
    },
    computed_teach_table_data() {
      return this.teach_table_data.map((ttb_data) => {
        if (!ttb_data.department_name_th)
          ttb_data.department_name_th = "[ แสดงในทุกภาควิชา ]";
        if (!ttb_data.department_name_en)
          ttb_data.department_name_en = "[ Shown in All Departments ]";
        if (!ttb_data.curriculum_name_th)
          ttb_data.curriculum_name_th = "[ แสดงในทุกสาขา ]";
        if (!ttb_data.curriculum_name_en)
          ttb_data.curriculum_name_en = "[ Shown in All Curriculums ]";

        if (!this.isUndergraduate) {
          ttb_data.class_th = "ระดับบัณฑิตศึกษา";
          ttb_data.class_en = "Postgraduate";
        } else if (ttb_data.class == 0) {
          ttb_data.class_th = "[ แสดงในทุกชั้นปี ]";
          ttb_data.class_en = "[ Shown in All Classes ]";
        } else {
          ttb_data.class_th = "ชั้นปีที่ " + ttb_data.class;
          switch (ttb_data.class) {
            case "1":
              ttb_data.class_en = "1<sup>st</sup> year";
              break;
            case "2":
              ttb_data.class_en = "2<sup>nd</sup> year";
              break;
            case "3":
              ttb_data.class_en = "3<sup>rd</sup> year";
              break;
            case "4":
              ttb_data.class_en = "4<sup>th</sup> year";
              break;
            case "5":
              ttb_data.class_en = "5<sup>th</sup> year";
              break;
            case "6":
              ttb_data.class_en = "6<sup>th</sup> year";
              break;
            default:
              ttb_data.class_en = "";
          }
        }

        const teachtable = ttb_data.teachtable.map((ttb) => {
          const data = ttb.data.map((x) => {

            const teach_time =
              x.teach_time && x.teach_time !== "00:00:00"
                ? String(x.teach_time).substring(0, 5)
                : null;
            const teach_time2 =
              x.teach_time2 && x.teach_time2 !== "00:00:00"
                ? String(x.teach_time2).substring(0, 5)
                : null;
            var classdatetime_str = (!this.formatDay(x.teach_day) || !teach_time || !teach_time2) ? "" : "<div>" + this.formatDay(x.teach_day) + " " + teach_time + "-" + teach_time2 + "</div>";

            const seenTeachTimes = new Set();
            if (classdatetime_str) {
              seenTeachTimes.add(`${Number(x.teach_day)}x${teach_time}-${teach_time2}`);
            }
            if (x.teachtime_str) {
              var teachtime_str_arr = x.teachtime_str.split(',');
              teachtime_str_arr.forEach((item) => {
                let [dayStr, timeRange] = item.split("x");
                if (!timeRange) return;
                const [start, end] = timeRange.split("-");
                if (!start || !end) return;
                timeRange = `${start.trim().substring(0, 5)}-${end.trim().substring(0, 5)}`;
                const timeKey = `${Number(dayStr)}x${timeRange}`;
                if (seenTeachTimes.has(timeKey)) return;
                seenTeachTimes.add(timeKey);
                const additionalTimePrefix = this.isUndergraduate ? "+ " : "";
                classdatetime_str += `<div>${additionalTimePrefix}${this.formatDay(dayStr)} ${timeRange}</div>`;
              });
            }
            x.classdatetime_table = classdatetime_str;

            x.href = this.$router.resolve({
              name: this.isUndergraduate ? "teach_table" : "graduate_teach_table",
              query: {
                level_id: this.level_id,
                mode: "by_subject_id",
                selected_year: this.selected_year,
                selected_semester: this.selected_semester,
                selected_class_year: this.selected_class_year,
                selected_faculty: this.selected_faculty,
                selected_department: this.selected_department,
                selected_curriculum: this.selected_curriculum,
                selected_branch: this.selected_branch,
                selected_subject_id: x.subject_id,
                search_all_faculty: this.search_all_faculty,
                search_all_department: this.search_all_department,
                search_all_curriculum: this.search_all_curriculum,
                search_all_branch: this.search_all_branch,
                search_all_class_year: this.search_all_class_year,
                selected_admission_type: this.selected_admission_type,
                selected_admission_semester: this.selected_admission_semester,
              },
            }).href;

            if (x.midterm_start_date_time) {
              x.midterm_date_formatted = dayjs(x.midterm_start_date_time)
                .locale(this.lang)
                .format(`dddd D MMMM ${this.lang == "th" ? "BBBB" : "YYYY"}`);

              x.midterm_time_formatted =
                dayjs(x.midterm_start_date_time).format("HH:mm") +
                " - " +
                dayjs(x.midterm_end_date_time).format("HH:mm");
            } else if (x.class_group_display) x.midterm_date_formatted = "";
            else
              x.midterm_date_formatted = this.$t(
                "pre_regis_add_subject.defined_by_instructor"
              );

            if (x.final_start_date_time) {
              x.final_date_formatted = dayjs(x.final_start_date_time)
                .locale(this.lang)
                .format(`dddd D MMMM ${this.lang == "th" ? "BBBB" : "YYYY"}`);

              x.final_time_formatted =
                dayjs(x.final_start_date_time).format("HH:mm") +
                " - " +
                dayjs(x.final_end_date_time).format("HH:mm");
            } else if (x.exam_text_locale_key) {
              x.final_date_formatted = this.$t(
                `teach_table.exam_text.${x.exam_text_locale_key}`
              );
              x.final_time_formatted = "";
            } else if (x.exam_text_detail) {
              x.final_date_formatted = x.exam_text_detail;
              x.final_time_formatted = "";
            } else if (x.class_group_display) x.final_date_formatted = "";
            else
              x.final_date_formatted = this.$t(
                "pre_regis_add_subject.defined_by_instructor"
              );

            return { ...x };
          });
          return { ...ttb, data };
        });
        return { ...ttb_data, teachtable };
      });
    },
    lang() {
      if (this.$root.$i18n.locale) return this.$root.$i18n.locale;
      return "th";
    },
    configurable() {
      return (
        ((this.config_permission.academic_year.some(
          (item) =>
            item.LEVEL_ID == this.level_id &&
            item.YEAR == this.selected_year &&
            item.SEMESTER == this.selected_semester
        ) || (this.config_permission.special_periods || []).some(
          (item) => item.LEVEL_ID == this.level_id &&
            item.FACULTY_ID == this.selected_faculty &&
            item.YEAR == this.selected_year && item.SEMESTER == this.selected_semester
        )) &&
          this.config_permission.user_permission.some(
            (item) =>
              item.LEVEL_ID == this.level_id &&
              (item.FACULTY_ID == this.selected_faculty ||
                item.FACULTY_ID == "AL")
          )) ||
        this.$store.state.user.is_authorized_officer === true
      );
    },
    configurableForClassroomAndBuilding() {
      return (
        this.config_permission.user_permission.some(
          (item) =>
            item.LEVEL_ID == this.level_id &&
            (item.FACULTY_ID == this.selected_faculty ||
              item.FACULTY_ID == "AL")
        ) || this.$store.state.user.is_authorized_officer === true
      );
    },
  },

  methods: {
    graduateHeadingLines(panel) {
      const title = this.objectTranslatorLowerCase(panel, 'curriculum_name') || '';
      return title.split(/,\s*(?=ประเภทการรับเข้า:|\[\s*(?:แสดงใน)?ทุกประเภทการรับเข้า\s*\]|Admission type:|\[All admission types\])/);
    },
    check_permission,
    downloadPdf,
    downloadExcel,
    downloadExcelAll,
    dayjs,
    objectTranslator,
    objectTranslatorLowerCase,

    cancelTeachTable(teach_table_id, subject_id, section, closed, registeredCount) {
      if (this.$refs.close_teach_table_dialog) {
        this.$refs.close_teach_table_dialog.open_dialog(
          teach_table_id,
          subject_id,
          section,
          closed,
          registeredCount
        );
      }
    },

    openTeacherDialog(teach_table_id) {
      this.fetchTeachers();
      if (this.$refs.config_teacher_dialog) {
        this.$refs.config_teacher_dialog.open_dialog(teach_table_id);
      }
    },

    openSubjectDialog(subject_id) {
      if (this.$refs.subject_dialog) {
        this.$refs.subject_dialog.show_item_detail(subject_id);
      }
    },

    openTimeSlotDialog(subject_id, teach_table_id, section) {
      if (this.$refs.timeslot_dialog) {
        this.$refs.timeslot_dialog.show_item_detail(
          subject_id,
          teach_table_id,
          section
        );
      }
    },

    goToAddTeachTable(panel) {
      let routeData = this.$router.resolve({
        name: "config_teach_table",
        query: {
          year: this.selected_year,
          semester: this.selected_semester,
          faculty_id: panel.faculty_id,
          dept_id: panel.department_id,
          curr2_id: panel.curriculum2_id,
          curr_id: this.isUndergraduate ? null : panel.curriculum2_id,
          branch_id: this.isUndergraduate ? null : panel.branch_id,
          admission_type: this.isUndergraduate ? null : panel.admission_type,
          admission_semester: this.isUndergraduate
            ? null
            : panel.admission_semester,
          class: this.isUndergraduate ? panel.class : null,
          level_id: this.level_id,
          command: "create",
        },
      });
      window.open(routeData.href, "_blank").focus();
    },

    goToEditTeachTable(teach_table_id, faculty_id) {
      let routeData = this.$router.resolve({
        name: "config_teach_table",
        query: {
          teach_table_id: teach_table_id,
          year: this.selected_year,
          semester: this.selected_semester,
          level_id: this.level_id,
          command: "update",
          ...(this.isUndergraduate ? { faculty_id } : {}),
        },
      });
      window.open(routeData.href, "_blank").focus();
    },

    goToCopyTeachTable(teach_table_id, faculty_id) {
      let routeData = this.$router.resolve({
        name: "config_teach_table",
        query: {
          teach_table_id: teach_table_id,
          year: this.selected_year,
          semester: this.selected_semester,
          level_id: this.level_id,
          command: "copy",
          permission_faculty_id: faculty_id,
        },
      });
      window.open(routeData.href, "_blank").focus();
    },

    async fetchTeachers() {
      const data = await tableTableService.getTeacherData();
      this.teacherData = data;
    },

    goToRoomShow: function (year, semester, building_no, room_no) {
      if (!year || !semester || !building_no || !room_no) return;
      const route = this.$router.resolve({
        name: "room_timetable",
        query: { year: String(year), semester: String(semester), building_no: String(building_no), room_no: String(room_no) },
      });
      window.open(route.href, "_blank", "noopener");
    },

    showRule(text) {
      this.ruleText = text;
      this.isRuleOpen = true;
    },

    shortRule(html) {
      const cutoff = "</div><div>";
      const index = html.indexOf(cutoff);
      if (index !== -1) {
        return html.substring(0, index + cutoff.length);
      } else {
        return "";
      }
    },

    formatDay(day) {
      day = Number(day);
      var d = "";
      if (this.lang == 'th') {
        switch (day) {
          case 1:
              d = "อาทิตย์";
              break;
          case 2:
              d = "จันทร์";
              break;
          case 3:
              d = "อังคาร";
              break;
          case 4:
              d = "พุธ";
              break;
          case 5:
              d = "พฤหัสบดี";
              break;
          case 6:
              d = "ศุกร์";
              break;
          case 7:
              d = "เสาร์";
              break;
          default:
              d = null;
              break;
        }
      } else {
        switch (day) {
          case 1:
              d = "Sunday";
              break;
          case 2:
              d = "Monday";
              break;
          case 3:
              d = "Tuesday";
              break;
          case 4:
              d = "Wednesday";
              break;
          case 5:
              d = "Thursday";
              break;
          case 6:
              d = "Friday";
              break;
          case 7:
              d = "Saturday";
              break;
          default:
              d = null;
              break;
        }
      }  
      return d;
    }
  },
  created() {},
};
</script>

<style lang="scss">
.tooltip {
  display: block !important;
  z-index: 10000;

  .tooltip-inner {
    background-color: rgba(0, 0, 0, 0.7) !important;
    color: white;
    border-radius: 16px;
    padding: 5px 10px 4px;
  }

  .tooltip-arrow {
    width: 0;
    height: 0;
    border-style: solid;
    position: absolute;
    margin: 5px;
    border-color: rgba(0, 0, 0, 0.7) !important;
    z-index: 1;
  }

  &[x-placement^="top"] {
    margin-bottom: 5px;

    .tooltip-arrow {
      border-width: 5px 5px 0 5px;
      border-left-color: transparent !important;
      border-right-color: transparent !important;
      border-bottom-color: transparent !important;
      bottom: -5px;
      left: calc(50% - 5px);
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  &[x-placement^="bottom"] {
    margin-top: 5px;

    .tooltip-arrow {
      border-width: 0 5px 5px 5px;
      border-left-color: transparent !important;
      border-right-color: transparent !important;
      border-top-color: transparent !important;
      top: -5px;
      left: calc(50% - 5px);
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  &[x-placement^="right"] {
    margin-left: 5px;

    .tooltip-arrow {
      border-width: 5px 5px 5px 0;
      border-left-color: transparent !important;
      border-top-color: transparent !important;
      border-bottom-color: transparent !important;
      left: -5px;
      top: calc(50% - 5px);
      margin-left: 0;
      margin-right: 0;
    }
  }

  &[x-placement^="left"] {
    margin-right: 5px;

    .tooltip-arrow {
      border-width: 5px 0 5px 5px;
      border-top-color: transparent !important;
      border-right-color: transparent !important;
      border-bottom-color: transparent !important;
      right: -5px;
      top: calc(50% - 5px);
      margin-left: 0;
      margin-right: 0;
    }
  }

  &.popover {
    $color: #f9f9f9;

    .popover-inner {
      background: $color;
      color: black;
      padding: 24px;
      border-radius: 5px;
      box-shadow: 0 5px 30px rgba(black, 0.1);
    }

    .popover-arrow {
      border-color: $color;
    }
  }

  &[aria-hidden="true"] {
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.15s, visibility 0.15s;
  }

  &[aria-hidden="false"] {
    visibility: visible;
    opacity: 1;
    transition: opacity 0.15s;
  }

  .last-cell {
    border-right-width: 1px;
  }
}

.rule .v-btn__content {
  color: #fff;
  font-size: 12px;
}
.and-text {
  color: #a67c00;
}
</style>
